import { Helmet } from 'react-helmet';
import { AffiliatePartnerGrid } from '@/components/affiliate/AffiliatePartnerGrid';
import { ComparisonTable } from '@/components/affiliate/ComparisonTable';
import { AffiliateHubStructuredData } from '@/components/structured-data/AffiliateHubStructuredData';
import { useAffiliatePartnersByCategory } from '@/hooks/useAffiliatePartners';
import { Plane, Hotel, Compass, Shield } from 'lucide-react';

export default function TravelResourcesPage() {
  const { data: featuredPartners } = useAffiliatePartnersByCategory('Featured');
  
  return (
    <>
      <Helmet>
        <title>Travel Resources & Partners | Trotting the World</title>
        <meta 
          name="description" 
          content="Discover our trusted travel partners for flights, hotels, tours, car rentals, and insurance. Book with confidence using our recommended services and start planning your next adventure." 
        />
        <meta name="keywords" content="travel resources, travel partners, book flights, hotel booking, travel tours, car rental, travel insurance, travel planning" />
        <link rel="canonical" href="https://trottingtheworld.com/travel-resources" />
      </Helmet>

      <AffiliateHubStructuredData />

      <div className="container mx-auto px-4 py-12 max-w-7xl">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Your Trusted Travel Partners
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            We've partnered with the best travel services to make your journeys easier, more affordable, and unforgettable. 
            Find everything you need for your next adventure in one place.
          </p>
          
          {/* Feature Icons */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto mt-8">
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-2">
                <Plane className="h-6 w-6 text-primary" />
              </div>
              <p className="text-sm font-medium">Best Flight Deals</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-2">
                <Hotel className="h-6 w-6 text-primary" />
              </div>
              <p className="text-sm font-medium">Top Hotels</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-2">
                <Compass className="h-6 w-6 text-primary" />
              </div>
              <p className="text-sm font-medium">Local Experiences</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-2">
                <Shield className="h-6 w-6 text-primary" />
              </div>
              <p className="text-sm font-medium">Travel Protection</p>
            </div>
          </div>
        </div>

        {/* Comparison Table - Show if we have featured partners */}
        {featuredPartners && featuredPartners.length > 0 && (
          <ComparisonTable 
            partners={featuredPartners}
            title="Compare Our Top Recommended Partners"
            description="We've tested these services extensively. Here's an honest comparison to help you choose."
          />
        )}

        {/* Partners Grid */}
        <AffiliatePartnerGrid />

        {/* Disclaimer */}
        <div className="mt-12 p-6 bg-muted rounded-lg">
          <p className="text-sm text-muted-foreground text-center">
            <strong>Disclosure:</strong> We may earn a commission when you book through these partners, 
            at no extra cost to you. This helps us continue creating free travel content and guides. 
            We only recommend services we trust and use ourselves.
          </p>
        </div>
      </div>
    </>
  );
}
